/**
 * Canonical shape and value scales for background removal.
 *
 * This lives in a leaf module (no worker, no canvas) so project state, action
 * handlers and the engine can all agree on one definition without pulling the
 * segmentation worker into every bundle that touches a clip.
 */

export type BackgroundMode =
  | "blur"
  | "color"
  | "image"
  | "video"
  | "transparent";

export interface BackgroundRemovalSettings {
  enabled: boolean;
  mode: BackgroundMode;
  /** Background blur radius in source pixels. */
  blurAmount: number;
  /** Replacement colour as a "#rrggbb" hex string. */
  backgroundColor: string;
  /**
   * Object URL for the replacement image. Object URLs are scoped to the page
   * that created them, so this is dropped before the settings reach project
   * state (see sanitizeBackgroundRemovalSettings). The decoded bitmap the
   * engine holds is what actually renders.
   */
  backgroundImageUrl?: string;
  /** Object URL for the replacement video, same lifetime rule as the image. */
  backgroundVideoUrl?: string;
  /** Matte feather radius in source pixels. */
  edgeBlur: number;
  /**
   * Matte cutoff, normalized 0..1. This is the canonical scale on every path:
   * project state, the engine map, and the mask maths that scales it to bytes.
   * The inspector is the only place that speaks percent, and it converts in
   * both directions at the slider.
   */
  threshold: number;
}

export const DEFAULT_BACKGROUND_SETTINGS: BackgroundRemovalSettings = {
  enabled: false,
  mode: "blur",
  blurAmount: 15,
  backgroundColor: "#00ff00",
  edgeBlur: 3,
  threshold: 0.7,
};

const isPersistableUrl = (url: unknown): url is string =>
  typeof url === "string" && url.length > 0 && !url.startsWith("blob:");

/**
 * Strips values that cannot outlive the current document before the settings
 * are stored on a clip. Key order is fixed and the result is idempotent, so a
 * caller can compare serialized copies to tell its own write apart from an
 * external one (undo, project load).
 */
export function sanitizeBackgroundRemovalSettings(
  settings: BackgroundRemovalSettings,
): BackgroundRemovalSettings {
  const sanitized: BackgroundRemovalSettings = {
    enabled: settings.enabled === true,
    mode: settings.mode,
    blurAmount: settings.blurAmount,
    backgroundColor: settings.backgroundColor,
    edgeBlur: settings.edgeBlur,
    threshold: settings.threshold,
  };
  if (isPersistableUrl(settings.backgroundImageUrl)) {
    sanitized.backgroundImageUrl = settings.backgroundImageUrl;
  }
  if (isPersistableUrl(settings.backgroundVideoUrl)) {
    sanitized.backgroundVideoUrl = settings.backgroundVideoUrl;
  }
  return sanitized;
}
